import React, { useState, useEffect } from "react";
import { Layout, Table, Tag, Button, Row, Col, Input, Card, message, Space, Dropdown } from "antd";
import { SearchOutlined, ClearOutlined, FilterOutlined, TruckOutlined, FilePdfOutlined, MoreOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";
import { getStatusColor } from "../../../utils/statusHelper";
import MRAcceptNotePreviewModal from "../../../components/material_requests/MRAcceptNotePreviewModal";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { isAnyPermissionAllowed } from "../../../utils/permissionHelper";

const { Content } = Layout;

const RequestAcceptList = () => {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filters
    const [searchFilter, setSearchFilter] = useState("");
    const [issuePreviewVisible, setIssuePreviewVisible] = useState(false);
    const [previewIssueMrId, setPreviewIssueMrId] = useState(null);

    // Applied Filters
    const [appliedSearch, setAppliedSearch] = useState("");

    useEffect(() => {
        fetchRequests();
    }, [appliedSearch]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const params = { tab: "issuable" };
            if (appliedSearch) params.search = appliedSearch;

            const response = await api.get("/inventory/material_request/accept_list.php", { params });
            if (response.data.status === "success") {
                setRequests(response.data.dataset || []);
            } else {
                message.error("Failed to fetch material requests.");
            }
        } catch (error) {
            message.error("Failed to connect to material requests endpoint.");
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = () => {
        setAppliedSearch(searchFilter);
    };

    const handleClear = () => {
        setSearchFilter("");
        setAppliedSearch("");
    };



    const columns = [
        {
            title: "Reference",
            dataIndex: "mr_ref",
            key: "mr_ref",
            render: (text) => <strong style={{ color: 'var(--fluid-primary)' }}>{text}</strong>
        },
        {
            title: "Required Date",
            dataIndex: "required_date",
            key: "required_date",
            render: (date) => dayjs(date).format("YYYY-MM-DD")
        },
        {
            title: "Status",
            dataIndex: "req_status",
            key: "req_status",
            render: (status) => (
                <Tag color={getStatusColor(status)} style={{ fontWeight: 600 }}>
                    {status}
                </Tag>
            )
        },
        {
            title: "Items Count",
            dataIndex: "total_items",
            key: "total_items",
            align: 'center',
        },
        {
            title: "Total Qty",
            dataIndex: "total_qty",
            key: "total_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Requested By",
            dataIndex: "creator_name",
            key: "creator_name",
            render: (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "-"
        },
        {
            title: "Requested Date",
            dataIndex: "req_dt",
            key: "req_dt",
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "-"
        },
        {
            title: "Action",
            key: "action",
            align: 'center',
            width: 80,
            render: (_, record) => {
                const canViewMR = isAnyPermissionAllowed(["material_request_list", "material_request_view_approvals"]);
                const items = [
                    {
                        key: "view",
                        label: "View Details",
                        icon: <EyeOutlined />,
                        onClick: () => {
                            if (canViewMR) {
                                navigate(`/inventory/material-request/${record.mr_id}/view`);
                            } else {
                                message.warning("You do not have permission to view Material Request details.");
                            }
                        }
                    },
                    {
                        key: "accept",
                        label: "Accept Items",
                        icon: <TruckOutlined />,
                        onClick: () => navigate(`/inventory/material-request/accept/${record.mr_id}`)
                    }
                ];

                if (record.req_status === 'Partially Accepted') {
                    items.push({
                        key: "print",
                        label: "Print",
                        icon: <FilePdfOutlined />,
                        onClick: () => {
                            setPreviewIssueMrId(record.mr_id);
                            setIssuePreviewVisible(true);
                        }
                    });
                }

                return (
                    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                        <Button type="text" icon={<MoreOutlined />} className="fluid-dots-btn" />
                    </Dropdown>
                );
            }
        }
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: 'Accept Items' },
                ]} />
                <h1 className="fluid-page-title">
                    <TruckOutlined style={{ marginRight: 12 }} />Material Request Accepts
                </h1>

            </div>

            <Content className="fluid-content">
                <Card className="fluid-card">
                    <div style={{ marginBottom: 20 }}>
                        <Row gutter={[16, 16]} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={10}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search Reference:</div>
                                <Input
                                    placeholder="MR-XXXXXXXX"
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    onPressEnter={handleFilter}
                                />
                            </Col>

                            <Col xs={24} sm={24} md={5}>
                                <div className="fluid-filter-actions" style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                    <Button
                                        type="primary"
                                        icon={<FilterOutlined />}
                                        onClick={handleFilter}
                                    >
                                        Filter
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClear}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={requests}
                        rowKey="mr_id"
                        loading={loading}
                        bordered
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            defaultPageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                        }}
                    />
                </Card>
            </Content>

            {/* Accept Note PDF Preview Modal */}
            {issuePreviewVisible && (
                <MRAcceptNotePreviewModal
                    mrId={previewIssueMrId}
                    visible={issuePreviewVisible}
                    onCancel={() => setIssuePreviewVisible(false)}
                />
            )}

            <Footerdiv />
        </Layout>
    );
};

export default RequestAcceptList;
